/**
 * L'assistant de saisie du planning, dans sa propre fenêtre.
 *
 * *Décisions de Chris, 23/10/2026 (docs/assistant-planning.md) :* le conducteur écrit en langage
 * naturel ce qu'il veut faire sur le planning ; l'assistant montre ce qu'il va changer et
 * **n'écrit qu'après un clic de confirmation** (D7). La fenêtre se pose sur un second écran, à
 * côté du planning (D11).
 *
 * ## Ce que la fenêtre fait, et ce qu'elle ne fait pas
 *
 * Elle envoie la phrase, affiche l'aperçu calculé par le serveur, et confirme. Elle ne calcule
 * rien elle-même : l'aperçu vient du code qui écrira (`assistant/transaction-partagee.ts`).
 *
 * Après une confirmation, elle **prévient les autres fenêtres** par le canal de `synchroVersion`
 * : le planning et la contre-étude se relisent seuls, sans être rouverts.
 *
 * Ouverte sur une version figée — ou devenue figée —, elle se met en pause et le dit (D3).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { JSX, KeyboardEvent } from "react";
import {
  abandonnerCommande,
  chargerVersion,
  confirmerCommande,
  envoyerCommande,
  etatAssistant,
} from "./api";
import type { ApercuAssistant, EtatAssistant, ReponseAssistant } from "./api";
import { versionDeLAdresse } from "./fenetreDuPlanning";
import { ecouterVersion, signalerVersionModifiee } from "./synchroVersion";

/** Un échange : la phrase, et ce qu'il en est advenu. */
type Echange = {
  readonly cle: number;
  readonly texte: string;
  readonly reponse: ReponseAssistant | null;
  /** Ce qui est arrivé APRÈS l'aperçu : confirmé, abandonné, refusé, ou un aperçu remis à jour. */
  readonly issue:
    | { readonly etat: "en_attente" }
    | { readonly etat: "confirmee"; readonly apercu: ApercuAssistant }
    | { readonly etat: "abandonnee" }
    | { readonly etat: "refusee"; readonly message: string }
    | { readonly etat: "erreur"; readonly message: string };
  /** L'aperçu à montrer : celui de la réponse, ou celui remis à jour si le planning a bougé. */
  readonly apercu: ApercuAssistant | null;
  readonly avertissement: string | null;
};

export function FenetreAssistant(): JSX.Element {
  const [versionId] = useState(() => versionDeLAdresse(window.location.search));
  const [etat, setEtat] = useState<EtatAssistant | null>(null);
  const [titre, setTitre] = useState<string>("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [texte, setTexte] = useState("");
  const [echanges, setEchanges] = useState<readonly Echange[]>([]);
  const [occupe, setOccupe] = useState(false);
  const compteur = useRef(0);
  const bas = useRef<HTMLDivElement | null>(null);
  const zone = useRef<HTMLTextAreaElement | null>(null);

  const relireEtat = useCallback((): void => {
    if (versionId === "") return;
    etatAssistant(versionId)
      .then(setEtat)
      .catch((e: unknown) => {
        setErreur(e instanceof Error ? e.message : String(e));
      });
  }, [versionId]);

  useEffect(() => {
    if (versionId === "") return;
    relireEtat();
    chargerVersion(versionId)
      .then((r) => {
        const libelle = r.version.libelle ?? r.version.type;
        setTitre(`${r.version.apo} — ${r.version.nomChantier} · ${libelle}`);
        document.title = `${r.version.apo} — Assistant`;
      })
      .catch(() => undefined);
  }, [versionId, relireEtat]);

  // Une version figée ailleurs pendant qu'on écrit : l'état se relit au signal du canal.
  useEffect(() => ecouterVersion(versionId, relireEtat), [versionId, relireEtat]);

  useEffect(() => {
    bas.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [echanges]);

  const modifier = (cle: number, f: (e: Echange) => Echange): void => {
    setEchanges((liste) => liste.map((e) => (e.cle === cle ? f(e) : e)));
  };

  const envoyer = (phrase: string): void => {
    const propre = phrase.trim();
    if (propre === "" || occupe) return;
    compteur.current += 1;
    const cle = compteur.current;
    setEchanges((liste) => [
      ...liste,
      { cle, texte: propre, reponse: null, issue: { etat: "en_attente" }, apercu: null, avertissement: null },
    ]);
    setTexte("");
    setOccupe(true);
    envoyerCommande(versionId, propre)
      .then((reponse) => {
        modifier(cle, (e) => ({ ...e, reponse, apercu: reponse.statut === "proposee" ? reponse.apercu : null }));
      })
      .catch((e: unknown) => {
        modifier(cle, (x) => ({
          ...x,
          issue: { etat: "erreur", message: e instanceof Error ? e.message : String(e) },
        }));
      })
      .finally(() => {
        setOccupe(false);
        relireEtat();
        zone.current?.focus();
      });
  };

  const confirmer = (echange: Echange): void => {
    if (echange.reponse === null) return;
    const id = echange.reponse.id;
    setOccupe(true);
    confirmerCommande(versionId, id)
      .then((r) => {
        if (r.statut === "confirmee") {
          modifier(echange.cle, (e) => ({ ...e, issue: { etat: "confirmee", apercu: r.apercu }, avertissement: null }));
          // Le planning et la contre-étude se relisent d'eux-mêmes (D11).
          signalerVersionModifiee(versionId);
        } else if (r.statut === "perime") {
          // Rien n'a été écrit (D8) : on remplace l'aperçu et on redemande la confirmation.
          modifier(echange.cle, (e) => ({ ...e, apercu: r.apercu, avertissement: r.erreur }));
        } else {
          modifier(echange.cle, (e) => ({ ...e, issue: { etat: "refusee", message: r.erreur } }));
        }
      })
      .catch((e: unknown) => {
        modifier(echange.cle, (x) => ({
          ...x,
          issue: { etat: "erreur", message: e instanceof Error ? e.message : String(e) },
        }));
      })
      .finally(() => {
        setOccupe(false);
        zone.current?.focus();
      });
  };

  const abandonner = (echange: Echange): void => {
    if (echange.reponse === null) return;
    void abandonnerCommande(versionId, echange.reponse.id).catch(() => undefined);
    modifier(echange.cle, (e) => ({ ...e, issue: { etat: "abandonnee" } }));
    zone.current?.focus();
  };

  /** Un candidat cliqué renvoie la phrase d'origine, précisée. */
  const preciser = (echange: Echange, candidat: string): void => {
    if (echange.reponse !== null) void abandonnerCommande(versionId, echange.reponse.id).catch(() => undefined);
    modifier(echange.cle, (e) => ({ ...e, issue: { etat: "abandonnee" } }));
    envoyer(`${echange.texte} — je parle de : ${candidat}`);
  };

  const surTouche = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Entrée envoie, Maj+Entrée va à la ligne : la convention de toutes les messageries.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      envoyer(texte);
    }
  };

  if (versionId === "") {
    return (
      <main className="fenetre-assistant">
        <p className="alerte" role="alert">
          Cette fenêtre s&apos;ouvre depuis le logo « Assistant » de la fenêtre du planning.
        </p>
      </main>
    );
  }

  const enPause = etat !== null && (!etat.configure || !etat.modifiable);

  return (
    <main className="fenetre-assistant">
      <header className="assistant-entete">
        <strong>Assistant de saisie</strong>
        <span className="assistant-titre">{titre}</span>
        {etat !== null && etat.configure && (
          <span className="assistant-compteur">
            {etat.utilisees} / {etat.limite} aujourd&apos;hui
          </span>
        )}
      </header>

      {erreur !== null && <p className="alerte">{erreur}</p>}
      {etat !== null && !etat.configure && (
        <p className="alerte">L&apos;assistant n&apos;est pas configuré sur ce serveur.</p>
      )}
      {etat !== null && etat.configure && !etat.modifiable && (
        <p className="alerte">
          Cette version est figée : l&apos;assistant ne travaille que sur la copie de travail ou la CE de
          suivi.
        </p>
      )}

      <div className="assistant-fil">
        {echanges.length === 0 && (
          <div className="assistant-aide">
            <p>Écrivez ce que vous voulez faire sur le planning, par exemple :</p>
            <ul>
              <li>« décale tout le chapitre reprofilage au 08/09 »</li>
              <li>« dans le reprofilage, livraison de 60 t de 0/31.5 le 07/09 avant 14h »</li>
              <li>« mets 2 pelles sur le terrassement »</li>
              <li>« repousse la barre du compactage d&apos;une journée »</li>
            </ul>
            <p>
              Rien n&apos;est écrit avant votre confirmation. La souris reste disponible avant et après.
            </p>
          </div>
        )}

        {echanges.map((e) => (
          <section key={e.cle} className="assistant-echange">
            <p className="assistant-phrase">{e.texte}</p>
            {e.reponse === null && e.issue.etat === "en_attente" && (
              <p className="assistant-attente">Lecture du planning…</p>
            )}
            {e.reponse !== null && <Reponse echange={e} occupe={occupe} onConfirmer={confirmer} onAbandonner={abandonner} onPreciser={preciser} />}
            {e.issue.etat === "erreur" && <p className="alerte">{e.issue.message}</p>}
          </section>
        ))}
        <div ref={bas} />
      </div>

      <footer className="assistant-saisie">
        <textarea
          ref={zone}
          value={texte}
          rows={3}
          autoFocus
          disabled={enPause}
          placeholder={enPause ? "Assistant en pause" : "Votre commande… (Entrée pour envoyer, Maj+Entrée pour aller à la ligne)"}
          onChange={(ev) => {
            setTexte(ev.target.value);
          }}
          onKeyDown={surTouche}
        />
        <button type="button" disabled={enPause || occupe || texte.trim() === ""} onClick={() => { envoyer(texte); }}>
          {occupe ? "…" : "Envoyer"}
        </button>
      </footer>
    </main>
  );
}

function Reponse(props: {
  echange: Echange;
  occupe: boolean;
  onConfirmer: (e: Echange) => void;
  onAbandonner: (e: Echange) => void;
  onPreciser: (e: Echange, candidat: string) => void;
}): JSX.Element {
  const { echange, occupe } = props;
  const r = echange.reponse;
  if (r === null) return <></>;

  if (r.statut === "question") {
    return (
      <div className="assistant-reponse question">
        <p>{r.message}</p>
        {echange.issue.etat === "en_attente" && r.candidats.length > 0 && (
          <div className="assistant-candidats">
            {r.candidats.map((c) => (
              <button key={c} type="button" className="lien" disabled={occupe} onClick={() => { props.onPreciser(echange, c); }}>
                {c}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (r.statut !== "proposee") {
    return (
      <div className="assistant-reponse refus">
        <p>{r.message}</p>
      </div>
    );
  }

  const apercu = echange.issue.etat === "confirmee" ? echange.issue.apercu : echange.apercu ?? r.apercu;
  return (
    <div className="assistant-reponse proposee">
      {r.message !== "" && <p>{r.message}</p>}
      {/* Le titre exact de chaque poste touché (D7) : c'est ce qui se relit avant de confirmer. */}
      <ul className="assistant-actions">
        {r.actions.map((a, n) => (
          <li key={n}>{a}</li>
        ))}
      </ul>
      {echange.avertissement !== null && <p className="alerte">{echange.avertissement}</p>}
      <TableApercu apercu={apercu} />
      {echange.issue.etat === "en_attente" && (
        <div className="assistant-boutons">
          <button type="button" className="principal" disabled={occupe} onClick={() => { props.onConfirmer(echange); }}>
            Confirmer
          </button>
          <button type="button" disabled={occupe} onClick={() => { props.onAbandonner(echange); }}>
            Annuler
          </button>
        </div>
      )}
      {echange.issue.etat === "confirmee" && <p className="assistant-ok">✓ Écrit dans le planning.</p>}
      {echange.issue.etat === "abandonnee" && <p className="assistant-annule">Annulé : rien n&apos;a été écrit.</p>}
      {echange.issue.etat === "refusee" && <p className="alerte">{echange.issue.message} Rien n&apos;a été écrit.</p>}
    </div>
  );
}

/**
 * Au-delà, le détail se replie : décaler un chapitre touche des dizaines de barres, et la
 * confirmation se perdrait sous un tableau qu'on fait défiler. Les totaux restent toujours dehors.
 */
const DETAIL_DEPLIE_MAX = 8;

function TableApercu({ apercu }: { apercu: ApercuAssistant }): JSX.Element {
  const detail = (
    <table className="assistant-apercu">
      <thead>
        <tr>
          <th>Ce qui change</th>
          <th>Avant</th>
          <th>Après</th>
        </tr>
      </thead>
      <tbody>
        {apercu.lignes.map((l, n) => (
          <tr key={n}>
            <td>
              <span className="assistant-quoi">{l.quoi}</span> {l.ou}
            </td>
            <td>{l.avant ?? <em>—</em>}</td>
            <td>{l.apres ?? <em>supprimée</em>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <>
      {apercu.lignes.length > DETAIL_DEPLIE_MAX ? (
        <details>
          <summary>Voir le détail : {apercu.lignes.length} éléments changent</summary>
          {detail}
        </details>
      ) : (
        detail
      )}
      {apercu.totaux.length > 0 && (
        <table className="assistant-apercu">
          <tbody>
            {apercu.totaux.map((t) => (
              <tr key={t.libelle} className="assistant-total">
                <td>{t.libelle}</td>
                <td>{t.avant}</td>
                <td>{t.apres}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
